/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable react/display-name */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import stylePropType from 'react-style-proptype';
import Tokens from 'styles/tokens';

export const CHECKBOX_STATES = {
  checked: 'checked',
  indeterminate: 'indeterminate',
  empty: 'empty'
};

const AvomaCheckbox = React.forwardRef((props, ref) => {
  const {
    id,
    name,
    checked,
    onChange,
    style,
    value,
    label,
    labelStyle,
    containerStyle,
    optional,
    disabled,
    tooltip,
    tooltipPosition,
    isDisabled,
    disabledStyles,
    isMultiSelect,
    multiSelectState,
    className = ''
  } = props;

  // Some use cases require a tooltip to be displayed on checkboxes to better identify their use.
  // Checkbox used in SnippetsPane for Select/Deselect All is an example of this
  const tooltipClasses = [];
  if (tooltip && tooltip.trim().length > 0) {
    tooltipClasses.push('tooltip icon-button-tooltip');
    tooltipClasses.push(tooltipPosition);
  }

  // To show the indeterminate state on the checkbox (hyphen) when some of the values are selected in multi select
  useEffect(() => {
    if (isMultiSelect && ref.current) {
      if (multiSelectState === CHECKBOX_STATES.checked) {
        ref.current.checked = true;
        ref.current.indeterminate = false;
      } else if (multiSelectState === CHECKBOX_STATES.empty) {
        ref.current.checked = false;
        ref.current.indeterminate = false;
      } else if (multiSelectState === CHECKBOX_STATES.indeterminate) {
        ref.current.indeterminate = true;
        ref.current.checked = false;
      }
    }
  }, [isMultiSelect, multiSelectState, ref]);

  // use the isDisabled prop if you want to add custom styling to disabled
  // checkboxes, since the default disabled greys it out.
  // Use case: UserCustomReminders.js -> sets a opacity of 0.5 when disabled thus retaining the blue color

  return (
    <div
      className={`${tooltipClasses.join(' ')} ${className}`}
      style={{
        ...styles.container,
        ...containerStyle
      }}
      aria-label={tooltip}
    >
      <input
        type='checkbox'
        id={id}
        name={name}
        value={value}
        checked={checked}
        onChange={isDisabled ? () => {} : onChange}
        style={{
          ...styles.checkbox,
          ...style,
          ...(disabled && { cursor: 'not-allowed' }),
          ...(isDisabled && { ...disabledStyles })
        }}
        ref={ref}
        disabled={disabled ? 'disabled' : ''}
      />
      <label
        style={{
          ...styles.label,
          ...labelStyle,
          ...(disabled && { cursor: 'default' })
        }}
        htmlFor={id}
      >
        {label}
        {optional && <span style={styles.optional}>(optional)</span>}
      </label>
    </div>
  );
});

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative'
  },
  label: {
    ...Tokens.type.bodyS,
    fontWeight: 'bold',
    marginLeft: Tokens.spacing.one,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    cursor: 'pointer'
  },
  checkbox: {
    width: Tokens.spacing.two,
    height: Tokens.spacing.two,
    flexShrink: 0,
    cursor: 'pointer'
  },
  optional: {
    marginLeft: Tokens.spacing.half,
    color: Tokens.colors.silver
  }
};

AvomaCheckbox.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  checked: PropTypes.bool,
  style: stylePropType,
  labelStyle: stylePropType,
  optional: PropTypes.bool,
  disabled: PropTypes.bool,
  containerStyle: stylePropType,
  tooltip: PropTypes.string,
  tooltipPosition: PropTypes.string,
  isDisabled: PropTypes.bool,
  disabledStyles: stylePropType,
  isMultiSelect: PropTypes.bool,
  multiSelectState: PropTypes.oneOf(Object.values(CHECKBOX_STATES)),
  className: PropTypes.string
};

AvomaCheckbox.defaultProps = {
  checked: false,
  style: null,
  labelStyle: null,
  optional: false,
  disabled: false,
  tooltip: '',
  tooltipPosition: 'bottom',
  isDisabled: false,
  isMultiSelect: false,
  multiSelectState: 'empty'
};

export default AvomaCheckbox;
